/**
 * BrandingShell — shared full-page wrapper for every public-facing tap flow.
 *
 * Centralizes:
 *   • Page background (wallpaper image OR variant-specific gradient fallback)
 *   • Dark readability overlay above the wallpaper
 *   • Vertical + horizontal centering of the content card
 *
 * Why this exists (v3.5.17):
 *   Each flow (SurveyPublic, ReviewShield, GlobalReview, LanguageGate) used to
 *   own its own gradient + min-h-screen wrapper. That led to TWO bugs:
 *     1. Survey wallpaper applied only to the question screen — the language
 *        gate (rendered by LanguageGate's own wrapper) bypassed it.
 *     2. ReviewShield + LanguageGate ended up with NESTED min-h-screen
 *        centers (v3.5.16 layout regression).
 *   By delegating the page chrome to ONE component, we stop duplicating
 *   layout code and stop drifting between flows.
 *
 * Usage:
 *   <BrandingShell wallpaperUrl={url} variant="dark"> ... card ... </BrandingShell>
 *
 * Rules:
 *   • Caller renders ONLY content (the card). BrandingShell handles centering.
 *   • Wallpaper is applied at the OUTER container — never inside the card.
 *   • The dark overlay (50% black) is mandatory when wallpaperUrl is set so
 *     text and buttons stay readable on bright photos.
 *   • Children are wrapped in a `relative z-10` div so they sit above the
 *     overlay without any extra plumbing from the caller.
 */
const VARIANT_GRADIENT = {
    dark: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #064e3b 100%)',
    danger: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #7f1d1d 100%)',
    light: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
};

const BrandingShell = ({
    wallpaperUrl = null,
    wallpaperZoom = null,
    variant = 'dark',
    centered = true,
    overlayOpacity = 0.5,
    className = '',
    contentClassName = '',
    children,
    'data-testid': testId = 'branding-shell',
}) => {
    const hasWallpaper = !!wallpaperUrl;
    // Zoom > 100 renders the wallpaper on a fixed layer with a transform scale
    // so aspect ratio is preserved. Default (null/100) keeps the exact
    // original rendering path — zero change for existing consumers.
    const zoom = hasWallpaper && Number(wallpaperZoom) > 100 ? Math.min(160, Number(wallpaperZoom)) : null;

    const baseClass = centered
        ? 'min-h-screen flex flex-col items-center justify-center px-5 py-8 relative'
        : 'min-h-screen relative';

    const bgStyle = hasWallpaper
        ? (zoom
            ? { background: VARIANT_GRADIENT[variant] || VARIANT_GRADIENT.dark }
            : {
                backgroundImage: `url("${wallpaperUrl}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
            })
        : { background: VARIANT_GRADIENT[variant] || VARIANT_GRADIENT.dark };

    return (
        <div
            className={`${baseClass} ${className}`.trim()}
            style={bgStyle}
            data-testid={testId}
            data-wallpaper={hasWallpaper ? 'true' : 'false'}
            data-variant={variant}
        >
            {zoom && (
                <div
                    aria-hidden="true"
                    className="fixed inset-0 pointer-events-none"
                    style={{
                        backgroundImage: `url("${wallpaperUrl}")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        transform: `scale(${zoom / 100})`,
                        transformOrigin: 'center',
                    }}
                    data-testid="branding-shell-zoom-layer"
                />
            )}            {hasWallpaper && (
                <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{ background: `rgba(0,0,0,${overlayOpacity})` }}
                    data-testid="branding-shell-overlay"
                />
            )}
            <div className={`relative z-10 w-full ${contentClassName}`.trim()}>
                {children}
            </div>
        </div>
    );
};

export default BrandingShell;
export { VARIANT_GRADIENT };
